import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { CheckboxComponent, DialogComponent, FileUploadComponent, UploadFile } from 'vcp-common-ui-lib';
import { translate } from '@jsverse/transloco';
import { MatHint } from '@angular/material/select';

/**
 * Multi-file upload dialog that wraps {@link FileUploadComponent} from vcp-common-ui-lib
 * (which embeds the upload list when {@link multiple} is true).
 *
 * Selecting files only lists them; uploads start when the primary action is triggered. The parent owns
 * the API calls and reports progress via {@link markUploading}, {@link markUploaded}, and {@link markFailed}.
 * Once every upload settles, the dialog shows its complete state for {@link completeDisplayMs} before
 * emitting {@link confirm} and closing.
 *
 * @example
 * <vc-multi-file-upload-dialog
 *   [(visible)]="show"
 *   [acceptedTypes]="['.jpg', '.png']"
 *   [maxFileSize]="10485760"
 *   (uploadFile)="uploadOne($event)"
 *   (retryFile)="uploadOne($event)"
 *   (cancelFile)="cancelOne($event)"
 *   (confirm)="onConfirm($event)">
 * </vc-multi-file-upload-dialog>
 */
@Component({
    selector: 'vc-multi-file-upload-dialog',
    imports: [DialogComponent, FileUploadComponent, CheckboxComponent, MatHint],
    templateUrl: './multi-file-upload-dialog.component.html',
    styleUrl: './multi-file-upload-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiFileUploadDialogComponent implements OnDestroy {
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private _uploadStarted: boolean = false;
    private _complete: boolean = false;
    private _completeTimer?: NodeJS.Timeout;
    private _previousSelection: File[] = [];
    zipExtractLabel: string = translate('MULTI_FILE_UPLOAD_DIALOG.ZIP_EXTRACT_LABEL');
    noFilesSelectedError: string = translate('MULTI_FILE_UPLOAD_DIALOG.NO_FILES_SELECTED');
    allFilesInvalidError: string = translate('MULTI_FILE_UPLOAD_DIALOG.ALL_FILES_INVALID');
    selectionError: string = '';
    showZipExtractCheckbox: boolean = false;
    extractionEnabled: boolean = false;

    @Input()
    set visible(value: boolean) {
        if (this._visible === value) return;
        this._visible = value;
        this.visibleChange.emit(value);
        if (!value) {
            this.reset();
        }
    }
    get visible(): boolean {
        return this._visible;
    }
    private _visible: boolean = false;

    @Input()
    title: string = translate('MULTI_FILE_UPLOAD_DIALOG.TITLE');

    @Input()
    description: string = translate('MULTI_FILE_UPLOAD_DIALOG.DESCRIPTION');

    @Input()
    primaryLabel: string = translate('MULTI_FILE_UPLOAD_DIALOG.PRIMARY_LABEL');

    @Input()
    secondaryLabel: string = translate('COMMON_UI.CANCEL');

    @Input()
    width: string = '640px';

    @Input()
    loading: boolean = false;

    @Input()
    showPrimary: boolean = true;

    @Input()
    closeOnPrimary: boolean = false;

    @Input()
    multiple: boolean = true;

    @Input()
    acceptedTypes!: string[];

    @Input()
    maxFileSize!: number;

    @Input()
    fileLimit!: number;

    @Input()
    dragFileLabel: string = translate('COMMON_UI.FILE_UPLOAD.DRAG_AND_DROP_FILE');

    @Input()
    selectFilesLabel: string = translate('COMMON_UI.FILE_UPLOAD.BROWSE_TO_UPLOAD');

    @Input()
    fileTypesLabel: string = translate('COMMON_UI.FILE_UPLOAD.FILE_TYPES');

    @Input()
    sizeMaxLabel: string = translate('COMMON_UI.FILE_UPLOAD.MAXIMUM_SIZE');

    @Input()
    maxFileSizeError: string = translate('COMMON_UI.FILE_UPLOAD.MAX_FILE_SIZE_ERROR');

    @Input()
    fileTypeError: string = translate('COMMON_UI.FILE_UPLOAD.FILE_TYPE_NOT_ACCEPTED');

    @Input()
    uploadingLabel: string = translate('COMMON_UI.FILE_UPLOAD.UPLOADING');

    /** Label shown in the dropzone once every upload has finished */
    @Input()
    completeLabel: string = translate('COMMON_UI.FILE_UPLOAD.COMPLETE_FILES_ADDED');

    /** How long the complete state stays visible before the dialog closes itself. */
    @Input()
    completeDisplayMs: number = 3000;

    /**
     * When true, the primary action emits {@link uploadFile} once per selected file.
     * When false, it emits {@link uploadAll} with the whole selection.
     */
    @Input()
    uploadIndividually: boolean = true;

    @Output()
    visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    /** Emitted per selected file when the primary action starts the upload and {@link uploadIndividually} is true. */
    @Output()
    uploadFile: EventEmitter<File> = new EventEmitter<File>();

    /** Emitted with the whole selection when the primary action starts the upload and {@link uploadIndividually} is false. */
    @Output()
    uploadAll: EventEmitter<File[]> = new EventEmitter<File[]>();

    /** Emitted with the successfully uploaded files after the complete state, just before the dialog closes. */
    @Output()
    confirm: EventEmitter<File[]> = new EventEmitter<File[]>();

    @Output()
    removeFile: EventEmitter<File> = new EventEmitter<File>();

    /** Emitted when an upload in flight is cancelled. */
    @Output()
    cancelFile: EventEmitter<File> = new EventEmitter<File>();

    /** Emitted when retry is clicked for a failed upload. */
    @Output()
    retryFile: EventEmitter<File> = new EventEmitter<File>();

    @ViewChild('fileUpload')
    private readonly fileUploadComponent?: FileUploadComponent;

    get uploads(): UploadFile[] {
        return this.fileUploadComponent?.uploads ?? [];
    }

    /** Primary stays disabled only while files are actively uploading. */
    get primaryDisabled(): boolean {
        return this.isUploading || this.isComplete;
    }

    /** True once the primary action started the upload and files are still being uploaded. */
    get isUploading(): boolean {
        return this._uploadStarted && !this._complete && this.hasInFlightUploads;
    }

    /** True while the complete state is held before the dialog closes. */
    get isComplete(): boolean {
        return this._complete;
    }

    /**
     * Files waiting on the parent to start their upload count as in flight, so a queued
     * upload does not read as finished before it begins.
     */
    get hasInFlightUploads(): boolean {
        return this.uploads.some(
            (upload: UploadFile): boolean => upload.status === 'uploading' || upload.status === 'pending'
        );
    }

    get uploadedFiles(): File[] {
        return this.uploads
            .filter((upload: UploadFile): boolean => upload.status === 'uploaded')
            .map((upload: UploadFile): File => upload.file);
    }

    get pendingFiles(): File[] {
        return this.uploads
            .filter((upload: UploadFile): boolean => upload.status === 'pending')
            .map((upload: UploadFile): File => upload.file);
    }

    get hasOnlyInvalidUploads(): boolean {
        return (
            this.uploads.length > 0 && this.uploads.every((upload: UploadFile): boolean => upload.status === 'error')
        );
    }

    onUploadsAdded(entries: UploadFile[]): void {
        this.selectionError = '';
        this.showZipExtractCheckbox =
            this.showZipExtractCheckbox ||
            entries.some((entry: UploadFile): boolean => entry.file.name.endsWith('.zip'));
        this._previousSelection = this.fileUploadComponent?.selection
            ? [...this.fileUploadComponent.selection]
            : this._previousSelection;
        this.changeDetectorRef.markForCheck();
    }

    /**
     * FileUpload embeds the list and handles remove/cancel internally; removals surface as selection changes.
     */
    onSelectionChange(selection: File[]): void {
        const removed: File[] = this._previousSelection.filter((file: File): boolean => !selection.includes(file));
        this._previousSelection = [...selection];

        removed.forEach((file: File): void => {
            if (this._uploadStarted) {
                this.cancelFile.emit(file);
            }
            this.removeFile.emit(file);
        });

        if (removed.length > 0) {
            this.checkUploadsSettled();
            this.changeDetectorRef.markForCheck();
        }
    }

    /** Retries from the embedded list emit {@link FileUploadComponent.uploadTriggered}. */
    onUploadTriggered(files: File[]): void {
        if (!this._uploadStarted) return;

        this.clearCompleteTimer();
        this._complete = false;
        files.forEach((file: File): void => this.retryFile.emit(file));
        this.changeDetectorRef.markForCheck();
    }

    /** Starts the upload for the selected files. */
    onPrimary(): void {
        const pending: File[] = this.pendingFiles;
        const hasOnlyInvalidUploads: boolean = pending.every((file: File): boolean =>
            this.uploads.some((upload: UploadFile): boolean => upload.file === file)
        );
        if (pending.length === 0) {
            this.selectionError = hasOnlyInvalidUploads ? this.allFilesInvalidError : this.noFilesSelectedError;
            this.changeDetectorRef.markForCheck();
            return;
        }

        this.selectionError = '';
        this._uploadStarted = true;
        this.changeDetectorRef.markForCheck();

        if (this.uploadIndividually) {
            pending.forEach((file: File): void => this.uploadFile.emit(file));
            return;
        }
        this.uploadAll.emit(pending);
    }

    onSecondary(): void {
        this.visible = false;
    }

    /** Moves a file into the uploading state. */
    markUploading(file: File): void {
        this.fileUploadComponent?.markUploading(file);
        this.changeDetectorRef.markForCheck();
    }

    /** Marks a file as successfully uploaded. */
    markUploaded(file: File): void {
        this.fileUploadComponent?.markUploaded(file);
        this.checkUploadsSettled();
        this.changeDetectorRef.markForCheck();
    }

    /** Marks a file as failed. Failures are retryable by default. */
    markFailed(file: File, error: string, retryable: boolean = true): void {
        this.fileUploadComponent?.markFailed(file, error, retryable);
        this.checkUploadsSettled();
        this.changeDetectorRef.markForCheck();
    }

    reset(): void {
        this.clearCompleteTimer();
        this._uploadStarted = false;
        this._complete = false;
        this._previousSelection = [];
        this.showZipExtractCheckbox = false;
        this.extractionEnabled = false;
        this.selectionError = '';
        this.fileUploadComponent?.reset();
        this.changeDetectorRef.markForCheck();
    }

    /** Holds the complete state once every upload settled with at least one success. */
    private checkUploadsSettled(): void {
        if (!this._uploadStarted || this._complete || this.hasInFlightUploads) return;
        if (this.uploadedFiles.length === 0) return;

        this._complete = true;
        this.changeDetectorRef.markForCheck();
        this._completeTimer = setTimeout((): void => this.finishUpload(), this.completeDisplayMs);
    }

    private finishUpload(): void {
        this.clearCompleteTimer();
        this.confirm.emit(this.uploadedFiles);
        this.visible = false;
    }

    private clearCompleteTimer(): void {
        if (this._completeTimer === undefined) return;
        clearTimeout(this._completeTimer);
        this._completeTimer = undefined;
    }

    ngOnDestroy(): void {
        this.clearCompleteTimer();
    }
}
